/**
 * TrlReactFlowCanvas — React Flow implementation of the swappable map canvas.
 *
 * TrlMapBuilder owns the domain graph (normalized coords, topology, images);
 * this component mirrors it into React Flow's node/edge state and lifts every
 * interaction back out. React Flow owns *transient* interaction state (live
 * drag position, selection), while the builder remains the source of truth for
 * everything durable. To avoid the two fighting, node `data` is only rebuilt
 * when a domain field that the node renders actually changes (see `nodeSig`),
 * and live drag positions are preserved across those rebuilds.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  ConnectionMode,
  useNodesState,
  useEdgesState,
  useStore,
  type Connection,
  type CoordinateExtent,
  type Edge,
  type Node,
  type NodeChange,
  type EdgeChange,
  type NodeTypes,
  type EdgeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { PlanetNode, type PlanetFlowNode, type PlanetNodeData } from './PlanetNode';
import { BezierMapEdge } from './BezierMapEdge';
import { edgeStyleToSvg, resolveTrlEdgeStyle } from '@swtorbit/shared-socket-events';
import { getMapBackgroundUrl } from '@swtorbit/shared-assets';
import type { CSSProperties } from 'react';
import type { TrlMapCanvasProps } from './types';

// A single non-interactive node spanning the whole 2K plane. It draws the map's
// perimeter line (request: a distinct dark-blue boundary) AND, because React
// Flow's MiniMap frames the bounding box of all nodes, it forces the minimap to
// represent exactly the 2560×1440 plane (not just the planet cluster). It is not
// draggable/selectable/deletable and passes pointer events through so pane
// clicks still deselect.
const BOUNDS_ID = '__map-bounds__';
type BoundsData = { width: number; height: number };
type BoundsFlowNode = Node<BoundsData, 'bounds'>;
type CanvasNode = PlanetFlowNode | BoundsFlowNode;

function MapBoundsNode() {
  // Intentionally invisible. The visible 2560×1440 perimeter is a *static* CSS
  // frame on the wrapper (see .trl-map-builder__canvas) which lives OUTSIDE the
  // pan/zoom transform, so it can never drift or get dragged out of place. This
  // node exists only to give React Flow's fit math and the MiniMap the true
  // plane rectangle to work against; it is sized explicitly by `boundsNode`.
  return <div style={{ width: '100%', height: '100%' }} />;
}

const nodeTypes: NodeTypes = { planet: PlanetNode, bounds: MapBoundsNode };
const edgeTypes: EdgeTypes = { bezier: BezierMapEdge };

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));

// Fallback dot size used to keep a node inside the plane *until React Flow has
// measured it* (see handleNodesChange, which then clamps by the real measured
// size so a dot can sit flush to all four plane edges). The planet node renders
// as a fixed-size 3rem (48px) dot.
const NODE_W = 48;
const NODE_H = 48;
const MAX_ZOOM = 2.5;

// d3-zoom (React Flow's pan engine) gets STUCK in a corner when `translateExtent`
// exactly equals the plane: at the zoom-out floor the viewport size equals the
// extent size, which is a razor's edge in its constrain() — sub-pixel rounding
// tips it into the edge-clamp branch, which snaps to a corner and locks there.
// Insetting the pan extent a few px INSIDE the plane keeps us firmly in d3's
// "viewport larger than extent → center & lock" branch: at the floor the plane
// sits centered and locked (no corner-jump, no void), and zoomed-in panning
// still reaches every planet (cards are clamped to canvas−NODE_W/H, far inside
// this inset). Small enough to be invisible; large enough to beat rounding noise.
const PAN_INSET = 8;

function CanvasInner({
  nodes,
  edges,
  background,
  canvasWidth,
  canvasHeight,
  selectedNodeId,
  readOnly = false,
  defaultEdgeStyle,
  onSelectNode,
  onMoveNode,
  onConnectNodes,
  onDeleteNode,
  onDeleteEdge,
  onEdgeControlPointsChange,
  onEdgeContextMenu,
  onOpenNode,
}: TrlMapCanvasProps) {
  const [rfNodes, setRfNodes, onRfNodesChange] = useNodesState<CanvasNode>([]);
  const [rfEdges, setRfEdges, onRfEdgesChange] = useEdgesState<Edge>([]);

  // Signature of everything a node *renders* (position excluded — that is
  // RF-owned during drag). When it changes we rebuild node data, preserving
  // any live-dragged positions for nodes that still exist.
  const nodeSig = useMemo(
    () =>
      nodes
        .map((n) =>
          [
            n.id,
            n.label,
            n.derivedLevel ?? '',
            n.isStart,
            n.isFinal,
            n.isCheckpoint,
            n.images.length,
            n.imagesToClear,
            n.images[0]?.imageUrl ?? '',
            n.icon ?? '',
            n.iconColor ?? '',
            n.id === selectedNodeId,
          ].join('|')
        )
        .join(';'),
    [nodes, selectedNodeId]
  );

  useEffect(() => {
    setRfNodes((prev) => {
      const prevPos = new Map(prev.map((p) => [p.id, p.position]));
      return nodes.map((n) => ({
        id: n.id,
        type: 'planet' as const,
        position: prevPos.get(n.id) ?? { x: n.posX * canvasWidth, y: n.posY * canvasHeight },
        selected: n.id === selectedNodeId,
        data: {
          label: n.label,
          derivedLevel: n.derivedLevel,
          isStart: n.isStart,
          isFinal: n.isFinal,
          isCheckpoint: n.isCheckpoint,
          thumbUrl: n.images[0]?.imageUrl,
          icon: n.icon ?? null,
          iconColor: n.iconColor ?? null,
          readOnly,
          onOpen: () => onOpenNode(n.id),
        } satisfies PlanetNodeData,
      }));
    });
    // nodeSig captures every rendered field; callbacks/dims are stable enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeSig, canvasWidth, canvasHeight, readOnly]);

  // The map-wide default rides in the signature too: edges that carry no style
  // of their own resolve against it, so changing it must repaint them.
  const edgeSig = useMemo(
    () =>
      [
        JSON.stringify(defaultEdgeStyle ?? null),
        edges
          .map((e) =>
            [e.id, e.fromNodeId, e.toNodeId, JSON.stringify(e.controlPoints), JSON.stringify(e.style ?? null)].join('|')
          )
          .join(';'),
      ].join('#'),
    [edges, defaultEdgeStyle]
  );

  useEffect(() => {
    setRfEdges(
      edges.map((e) => ({
        id: e.id,
        source: e.fromNodeId,
        target: e.toNodeId,
        type: 'bezier',
        // Layer the edge's own style over the map-wide default, falling back to
        // the built-in white look. An explicit stroke is required so lines stay
        // visible in readOnly mode — without it React Flow's default stroke is
        // driven by interaction state and disappears when elementsSelectable is
        // false (locked map).
        style: edgeStyleToSvg(resolveTrlEdgeStyle(e.style, defaultEdgeStyle)),
        data: {
          controlPoints: e.controlPoints,
          canvasWidth,
          canvasHeight,
          readOnly,
          onControlPointsChange: (pts: Array<{ x: number; y: number }>) =>
            onEdgeControlPointsChange(e.id, pts),
        },
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edgeSig, canvasWidth, canvasHeight, readOnly]);

  // Route RF node changes: deletions go to the domain (which rebuilds this
  // node set), everything else (drag/select) stays RF-local. The decorative
  // bounds node is filtered out — RF measures it (a 'dimensions' change) but it
  // isn't part of the managed planet set, so its changes must not be applied.
  const handleNodesChange = useCallback(
    (changes: NodeChange<CanvasNode>[]) => {
      if (readOnly) return;
      const own = changes.filter((c) => !('id' in c && c.id === BOUNDS_ID));
      const removals = own.filter((c) => c.type === 'remove');
      removals.forEach((c) => onDeleteNode(c.id));
      // Clamp live drags so the WHOLE card stays inside the 2560×1440 plane, using
      // each card's *measured* size so it can be dragged flush to every edge
      // regardless of its content-driven height. (A global nodeExtent can't do
      // this — it insets by one fixed size, stranding shorter cards short of the
      // bottom/right edges, which is exactly the bug being fixed.)
      const rest = own
        .filter((c) => c.type !== 'remove')
        .map((c) => {
          if (c.type !== 'position' || !c.position) return c;
          const measured = rfNodes.find((n) => n.id === c.id)?.measured;
          const w = measured?.width ?? NODE_W;
          const h = measured?.height ?? NODE_H;
          return {
            ...c,
            position: {
              x: Math.max(0, Math.min(c.position.x, canvasWidth - w)),
              y: Math.max(0, Math.min(c.position.y, canvasHeight - h)),
            },
          };
        });
      if (rest.length) onRfNodesChange(rest);
    },
    [readOnly, onDeleteNode, onRfNodesChange, rfNodes, canvasWidth, canvasHeight]
  );

  const handleEdgesChange = useCallback(
    (changes: EdgeChange<Edge>[]) => {
      if (readOnly) return;
      const removals = changes.filter((c) => c.type === 'remove');
      removals.forEach((c) => onDeleteEdge(c.id));
      const rest = changes.filter((c) => c.type !== 'remove');
      if (rest.length) onRfEdgesChange(rest);
    },
    [readOnly, onDeleteEdge, onRfEdgesChange]
  );

  const handleConnect = useCallback(
    (c: Connection) => {
      if (readOnly || !c.source || !c.target) return;
      onConnectNodes(c.source, c.target);
    },
    [readOnly, onConnectNodes]
  );

  // Set while a node drag is in flight, so the click that follows the drop does
  // not open the planet editor (see onNodeClick).
  const justDragged = useRef(false);

  // Pan is bound just INSIDE the plane (see PAN_INSET), so the view can never
  // move outside the 2560×1440 perimeter and — critically — at the zoom-out
  // floor the viewport is larger than this extent, so d3-zoom centers & locks
  // it instead of snapping into a corner. The inset is a few px: invisible, but
  // enough to keep us off d3's razor edge.
  const translateExtent = useMemo<CoordinateExtent>(
    () => [
      [PAN_INSET, PAN_INSET],
      [canvasWidth - PAN_INSET, canvasHeight - PAN_INSET],
    ],
    [canvasWidth, canvasHeight]
  );

  // Zoom-out floor: the exact plane-fit zoom, so the whole 2560×1440 plane fills
  // the 16:9 frame edge-to-edge and the admin can never zoom out past it. The
  // frame scales with the page, so recompute on resize.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [minZoom, setMinZoom] = useState(0.1);
  const [floorMeasured, setFloorMeasured] = useState(false);
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const recompute = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w === 0 || h === 0) return;
      const planeFit = Math.min(w / canvasWidth, h / canvasHeight);
      if (planeFit > 0) {
        setMinZoom(planeFit);
        setFloorMeasured(true);
      }
    };
    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [canvasWidth, canvasHeight]);

  // The plane-boundary node (see BOUNDS_ID). Rendered first so planet cards sit
  // on top of its perimeter line; non-interactive so it never intercepts drags
  // or pane clicks.
  const boundsNode = useMemo<BoundsFlowNode>(
    () => ({
      id: BOUNDS_ID,
      type: 'bounds',
      position: { x: 0, y: 0 },
      data: { width: canvasWidth, height: canvasHeight },
      draggable: false,
      selectable: false,
      connectable: false,
      deletable: false,
      focusable: false,
      zIndex: 0,
      // Explicit size so React Flow's fit/clamp math and the MiniMap know the
      // real 2560×1440 plane WITHOUT relying on DOM measurement (whose change we
      // filter out in handleNodesChange). This is what makes the zoom-out floor
      // fit the whole plane instead of pulling the view toward the origin.
      width: canvasWidth,
      height: canvasHeight,
      style: { width: canvasWidth, height: canvasHeight, pointerEvents: 'none' },
    }),
    [canvasWidth, canvasHeight]
  );
  const allNodes = useMemo(() => [boundsNode, ...rfNodes], [boundsNode, rfNodes]);

  // Show the minimap only while zoomed in; at the zoom-out floor the whole plane
  // already fills the frame, so the minimap is redundant and fades away. `zoom`
  // is read reactively from the store; `minZoom` is the fit-the-plane floor.
  const zoom = useStore((s) => s.transform[2]);
  const zoomedIn = floorMeasured && zoom > minZoom * 1.08;

  // Recolourable backdrop behind the dotted grid. The chosen SVG is a CSS mask
  // the tint colour paints through; colour + opacity are admin-authored. Drawn
  // OUTSIDE React Flow's pan/zoom transform (like the perimeter frame), so it
  // aligns with the plane at the zoom-out floor. Undefined when no image is set.
  const bgStyle = useMemo<CSSProperties | null>(() => {
    if (!background?.image) return null;
    const url = getMapBackgroundUrl(background.image);
    if (!url) return null;
    return {
      '--trl-mapbg-src': `url("${url}")`,
      '--trl-mapbg-color': background.color,
      '--trl-mapbg-opacity': background.opacity,
    } as CSSProperties;
  }, [background?.image, background?.color, background?.opacity]);

  return (
    <div ref={wrapperRef} className="trl-map-builder__canvas rounded-xs overflow-hidden">
      {bgStyle && <div className="trl-map-builder__bg" style={bgStyle} />}
      <ReactFlow
        nodes={allNodes}
        edges={rfEdges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={handleConnect}
        onNodeDragStart={() => {
          justDragged.current = true;
        }}
        onNodeDragStop={(_e, node) =>
          onMoveNode(node.id, clamp01(node.position.x / canvasWidth), clamp01(node.position.y / canvasHeight))
        }
        onNodeClick={(e, node) => {
          onSelectNode(node.id);
          if (node.id === BOUNDS_ID) return;
          // A drag ends with mouseup on the dot, so the browser fires a click
          // too — without this, every reposition would open the editor. RF's
          // nodeDragThreshold means a real click never fires onNodeDragStart.
          if (justDragged.current) {
            justDragged.current = false;
            return;
          }
          // Same for a connect-drag that starts and ends on the ring: that is an
          // aborted connection, not a request to open the planet.
          if ((e.target as HTMLElement).closest('.react-flow__handle')) return;
          onOpenNode(node.id);
        }}
        onEdgeContextMenu={(e, edge) => {
          if (readOnly) return;
          // Suppress the browser context menu and open our path style/delete modal.
          e.preventDefault();
          onEdgeContextMenu(edge.id);
        }}
        onPaneClick={() => onSelectNode(null)}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable={!readOnly}
        connectionMode={ConnectionMode.Loose}
        connectionRadius={30}
        deleteKeyCode={readOnly ? null : ['Delete', 'Backspace']}
        translateExtent={translateExtent}
        minZoom={minZoom}
        maxZoom={MAX_ZOOM}
        fitView
        fitViewOptions={{ padding: 0 }}
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        <Controls showInteractive={!readOnly} />
        {/* Overview of the true 2560×1440 plane. The full-plane bounds node makes
            the minimap frame exactly the map (16:9) with no padding (offsetScale
            0); it is drawn transparent there so only the planets show. Colours
            mirror the SCSS galaxy palette ($color-dark / _cyan_medium /
            _cyan_light) — the minimap draws them via SVG props, not CSS, so its
            default light background would otherwise render as a white block. It
            fades out at the zoom-out floor (see `zoomedIn`). */}
        <MiniMap
          pannable
          zoomable
          style={{ width: 224, height: 126 }}
          offsetScale={0}
          bgColor="#010a14"
          maskColor="rgba(1, 10, 20, 0.6)"
          nodeColor={(n) => (n.id === BOUNDS_ID ? 'transparent' : '#0f4d76')}
          nodeStrokeColor={(n) => (n.id === BOUNDS_ID ? 'transparent' : '#71d9ff')}
          className={
            zoomedIn
              ? 'trl-map-builder__minimap'
              : 'trl-map-builder__minimap trl-map-builder__minimap--hidden'
          }
        />
      </ReactFlow>
    </div>
  );
}

export function TrlReactFlowCanvas(props: TrlMapCanvasProps) {
  return (
    <ReactFlowProvider>
      <CanvasInner {...props} />
    </ReactFlowProvider>
  );
}
